import styled from '@emotion/styled'
import { alpha, Box, useMediaQuery } from '@mui/material'
import React, { useCallback } from 'react'

import { DesktopOnly } from '@f-fertighaus/applications/Next/components/common/MediaQuery'
import { BauenIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/BauenIcon'
import { BungalowIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/BungalowIcon'
import { EinfamilienhausIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/EinfamilienhausIcon'
import { FertighausIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/FertighausIcon'
import { ImmonetIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/ImmonetIcon'
import { ImmoweltIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/ImmoweltIcon'
import { MassivhausIcon } from '@f-fertighaus/applications/Next/components/fertighaus/Icon/MassivhausIcon'
import { IMAGE_ASPECT_RATIO } from '@f-fertighaus/applications/Next/config/constants'
import { useHouseDetail } from '@f-fertighaus/applications/Next/utils/context/HouseDetail'
import { transientOptions } from '@f-fertighaus/applications/Next/utils/transientOptions'
import { webtrack } from '@f-fertighaus/applications/Next/utils/webtrack'
import { FavoriteButtonWithText } from '@f-fertighaus/applications/Next/views/houseDetailPage/houseImageCarousel/components/FavoriteButtonWithText'
import { FloorPlansButton } from '@f-fertighaus/applications/Next/views/houseDetailPage/houseImageCarousel/components/FloorPlansButton'
import {
  ClickableImageSource,
  useOpenImageGallery
} from '@f-fertighaus/applications/Next/views/houseDetailPage/houseImageCarousel/HouseImageCarousel'

export const HouseImagesSection = () => {
  const {
    state: { floor_plans, exterior_images, interior_images, active },
    featureFlags,
    domain
  } = useHouseDetail()
  const isDesktopView = useMediaQuery('(min-width: 900px)')

  const { onFloorPlanImageClick, onExteriorImageClick } = useOpenImageGallery()
  const computeImagesToShow = useCallback(() => {
    const MAX_ARRAY_LENGTH = 3
    const fillWithExteriorImages = (): ClickableImageSource[] =>
      exterior_images.slice(0, MAX_ARRAY_LENGTH).map(el => ({
        ...el,
        handleOpenGallery: onExteriorImageClick
      }))
    const fillWithInteriorImages = (arr: ClickableImageSource[]) => {
      const interior = interior_images
        .slice(0, MAX_ARRAY_LENGTH - arr.length)
        .map(el => ({
          ...el,
          handleOpenGallery: onExteriorImageClick
        }))
      return [...arr, ...interior]
    }
    const fillWithFloorPlanImages = (arr: ClickableImageSource[]) => {
      if (floor_plans?.length) {
        const MIN_FLOOR_PLANS_TO_DISPLAY = 1
        const floorPlanImages = floor_plans
          .slice(0, MAX_ARRAY_LENGTH - arr.length || MIN_FLOOR_PLANS_TO_DISPLAY)
          .map(floor_plan => ({
            ...floor_plan,
            handleOpenGallery: onFloorPlanImageClick
          }))
        return arr.length === MAX_ARRAY_LENGTH
          ? [...arr].map((el, index) =>
              index === MAX_ARRAY_LENGTH - 1 ? floorPlanImages[0] : el
            )
          : [...arr, ...floorPlanImages]
      }
      return arr
    }
    const fillWithPlaceholderImages = (arr: ClickableImageSource[]) => {
      const placeholderImages = new Array(MAX_ARRAY_LENGTH - arr.length)
        .fill(null)
        .map(() => ({
          ...arr[0],
          isPlaceholder: true
        }))
      return [...arr, ...placeholderImages]
    }

    return fillWithPlaceholderImages(
      fillWithFloorPlanImages(fillWithInteriorImages(fillWithExteriorImages()))
    )
  }, [
    exterior_images,
    interior_images,
    floor_plans,
    onExteriorImageClick,
    onFloorPlanImageClick
  ])

  const images = computeImagesToShow()

  const trackImageClick = (imgUrl: string) =>
    webtrack({
      category: 'image',
      action: 'click',
      label: 'view-pictures-floor-plans',
      location: 'image-tiles',
      values: [{ type: 'url', value: imgUrl }]
    })

  if (images.filter(Boolean).length === 0) {
    return null
  }

  const showAddToFavourite = !featureFlags.includes(
    '@FF_DISABLE_ADD_TO_FAVOURITE'
  )

  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === 'Space') {
      event.preventDefault()
      event.target.click()
    }
  }

  return (
    <ImageGalleryWrapper>
      {images.map(image => (
        <ImageWrapper
          key={image['max-1600x1200.watermarked']}
          onKeyDown={handleKeyDown}
          onClick={() => {
            trackImageClick(image.original)
            image.handleOpenGallery()
          }}
          $isPlaceholder={image.isPlaceholder}>
          {isDesktopView && (
            <Img
              srcSet={`${image['fill-320x240']} 450w,
             ${image['width-640x480']} 600w,
             ${image['width-1280x960']} 1000w,
             ${image['max-1600x1200.watermarked']} 1600w,
             ${image.original} 2048w`}
              sizes={'70vw'}
              src={`${image.original}`}
              alt={image.alt_text}
              title={image.title}
            />
          )}
          {image.isPlaceholder && (
            <ImagePlaceholderBackgroundLayer
              onClick={(event: React.MouseEvent<HTMLImageElement>) => {
                trackImageClick(event.currentTarget.src)
                image.handleOpenGallery()
              }}>
              {{
                fertighaus: () => <FertighausIcon />,
                massivhaus: () => <MassivhausIcon />,
                einfamilienhaus: () => <EinfamilienhausIcon />,
                bungalow: () => <BungalowIcon />,
                bauen: () => <BauenIcon />,
                immowelt: () => <ImmoweltIcon />,
                immonet: () => <ImmonetIcon />
              }[domain]() || (() => null)()}
            </ImagePlaceholderBackgroundLayer>
          )}
        </ImageWrapper>
      ))}
      <Box displayPrint='none'>
        {Boolean(floor_plans) && <FloorPlansButton />}
        {showAddToFavourite && active && <FavoriteButtonWithText />}
      </Box>
    </ImageGalleryWrapper>
  )
}

const GRID_GAP = 8

const ImageGalleryWrapper = styled(DesktopOnly)`
  position: relative;
  grid-area: gallery;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, 1fr);
  column-gap: ${GRID_GAP}px;
  row-gap: ${GRID_GAP}px;
  background-color: ${({ theme }) => theme.palette.common.white};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: ${({ theme }) => theme.spacing(4)};
    display: grid;
  }

  @media print {
    display: grid;
  }
`

const Img = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  aspect-ratio: calc(${IMAGE_ASPECT_RATIO});
  object-fit: cover;
`

const ImageWrapper = styled('button', transientOptions)<{
  $isPlaceholder?: boolean
}>`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: pointer;
  overflow: hidden;
  background-color: ${({ theme }) => theme.palette.glass['500']};
  border: none;
  padding: 0;

  :first-of-type {
    align-self: stretch;
    padding-bottom: calc(100% / ${IMAGE_ASPECT_RATIO});
    grid-area: 1 / 1 / 3 / 3;
    border-radius: var(--border-radius-card-large)
      var(--border-radius-card-medium) var(--border-radius-card-medium)
      var(--border-radius-card-large);
  }

  :nth-of-type(2) {
    grid-area: 1 / 3 / 2 / 4;
    border-radius: var(--border-radius-card-medium)
      var(--border-radius-card-large) var(--border-radius-card-medium)
      var(--border-radius-card-medium);
  }

  :nth-of-type(3) {
    grid-area: 2 / 3 / 3 / 4;
    border-radius: var(--border-radius-card-medium)
      var(--border-radius-card-medium) var(--border-radius-card-large)
      var(--border-radius-card-medium);
  }
`

const ImagePlaceholderBackgroundLayer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background-color: ${({ theme }) => alpha(theme.palette.glass['500'], 0.75)};

  > svg {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 1;
    transform: translate(-50%, -50%);
  }
`
